/**
 * The platform language catalogue inside the account hub - the DYNAMIC source
 * of truth for the language picker.
 *
 * A superadmin manages the catalogue in the admin centre (Settings -> Languages,
 * host view); what is read here is the PUBLIC mirror
 * (`/api/public/platform-locales`), which also says, per language, whether a
 * system translation of the account interface exists (`system.account`). The
 * picker (LanguageMenu) shows ONLY languages whose account UI is translated
 * (the base locales et/en always are) - otherwise a user would pick a language
 * whose content is still Estonian.
 *
 * Fallback (API down, or the first paint): the base locales et/en only. Module
 * cache: one fetch per session.
 */
import { useEffect, useState } from "react";
import { api } from "./api";

export interface PlatformLocaleInfo {
  /** ISO 639-1 code (for example "pl"). */
  code: string;
  /** The name in the language itself (its autonym). */
  label: string;
  /** Whether a system UI translation exists (always true for the base locales). */
  system: { site: boolean; admin: boolean; account: boolean };
}

/** The static fallback - the hub's base locales (the file-based catalogues). */
const FALLBACK: PlatformLocaleInfo[] = [
  { code: "et", label: "Eesti", system: { site: true, admin: true, account: true } },
  { code: "en", label: "English", system: { site: true, admin: true, account: true } },
];

let cache: PlatformLocaleInfo[] | null = null;
let inflight: Promise<PlatformLocaleInfo[]> | null = null;

function fetchPlatformLocales(): Promise<PlatformLocaleInfo[]> {
  if (cache) return Promise.resolve(cache);
  inflight ??= api
    .get<{ locales: PlatformLocaleInfo[] }>("/public/platform-locales")
    .then((r) => {
      cache = Array.isArray(r?.locales) && r.locales.length ? r.locales : FALLBACK;
      return cache;
    })
    // A failure falls back but is NOT cached, so the next caller retries.
    .catch(() => FALLBACK)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** The platform catalogue, reactive - the fallback until it arrives. */
export function usePlatformLocales(): { locales: PlatformLocaleInfo[]; loading: boolean } {
  const [locales, setLocales] = useState<PlatformLocaleInfo[]>(cache ?? FALLBACK);
  const [loading, setLoading] = useState(!cache);
  useEffect(() => {
    let alive = true;
    void fetchPlatformLocales().then((l) => {
      if (alive) {
        setLocales(l);
        setLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, []);
  return { locales, loading };
}

/** Label lookup: the dynamic catalogue, else the code in upper case. */
export function platformLocaleLabel(locales: PlatformLocaleInfo[], code: string): string {
  return locales.find((l) => l.code === code)?.label ?? code.toUpperCase();
}
